import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { isProjectType, type ProjectType } from "@reno-estimator/core";
import App from "./App.js";
import "./styles/app.wp.css";

/**
 * WordPress embed entry point (Phase 18). Distinct from main.tsx (the
 * standalone dev-server entry, unmodified) because a WordPress page can
 * contain zero, one, or several estimator widgets (e.g. a shortcode on one
 * page and a Gutenberg block on another, or two different `project=`
 * instances), so this mounts a separate React root per
 * `[data-reno-estimator]` element found in the page rather than assuming
 * one fixed #root element exists - the classic-app assumption main.tsx
 * makes and which a WordPress-rendered page can't guarantee.
 *
 * Each mount point may carry a `data-project` attribute (from the
 * shortcode's `project="kitchen"` attribute or the block's equivalent),
 * read here and passed through as App's initialProjectType prop - see
 * useEstimatorFlow.ts's UseEstimatorFlowOptions for what that does.
 *
 * Imports the WordPress-scoped stylesheet (styles/app.wp.css) instead of
 * the standalone app's styles/app.css - see that file for why a plain
 * theme-independent embed can't reuse globally-scoped CSS as-is.
 */

function readInitialProjectType(node: HTMLElement): ProjectType | undefined {
  const raw = node.dataset.project;
  if (!raw) return undefined;
  return isProjectType(raw) ? raw : undefined;
}

function mountOne(node: HTMLElement): void {
  if (node.dataset.renoMounted === "true") return;
  node.dataset.renoMounted = "true";
  const initialProjectType = readInitialProjectType(node);
  createRoot(node).render(
    <StrictMode>
      <App initialProjectType={initialProjectType} />
    </StrictMode>
  );
}

function mountAll(): void {
  document.querySelectorAll<HTMLElement>("[data-reno-estimator]").forEach(mountOne);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", mountAll);
} else {
  mountAll();
}

// Gutenberg's editor preview and some page builders re-render/replace DOM
// after the initial script evaluation (e.g. inserting the block, or an
// Elementor "render on demand" panel). A small MutationObserver on <body>
// catches any [data-reno-estimator] element that appears later, without
// polling. Mounting is idempotent (guarded by data-reno-mounted above), so
// this never double-mounts an already-live widget.
//
// Microtask-debounced: a real WordPress page can have its own chatty
// mutation sources (a chat widget, ad slots, analytics) firing many DOM
// mutations per second, and each one would otherwise trigger its own full
// document.querySelectorAll() scan. Coalescing same-tick mutations into a
// single mountAll() keeps this from becoming a background performance cost
// on pages that have nothing to do with the estimator.
if (typeof MutationObserver !== "undefined" && document.body) {
  let scanScheduled = false;
  const scheduleMountAll = () => {
    if (scanScheduled) return;
    scanScheduled = true;
    queueMicrotask(() => {
      scanScheduled = false;
      mountAll();
    });
  };
  const observer = new MutationObserver(() => scheduleMountAll());
  observer.observe(document.body, { childList: true, subtree: true });
}
